import Link from '@/components/Link/Link';
import { type SerializedStyles } from '@emotion/react';
import tw, { styled, css } from 'twin.macro';
import KontentImage from '@/components/KontentImage/KontentImage';

export const StyledHeadline = styled.h3`
  ${tw`font-poppins font-semi-bold`}
  ${tw`text-heading-xs text-primary-p1`}
`;

export const StyledBody = styled.div`
  ${tw`font-inter`}
  ${tw`text-body-m text-primary-p1`}

  // NOTE: Let the Building Blocks manage the spacing between them by avoiding margin on the first child
  // Our compiler does not like first-child or :nth-child, and the "of-type" pseudo-class has a different effect
  // also the way our rich text css is applied it requires more specificity to take effect in some cases (not worth fixing before moving from gatsby)
  // hence this awkward owl
  & > *:not(* + *) {
    ${tw`!mt-0`}
  }

  a {
    ${tw`text-secondary-s1`}
    ${tw`hover:underline no-underline`}
  }

  p {
    ${tw`mt-[14px] last:mb-[14px]`}
  }
`;

export const StyledButton = styled(Link)`
  ${tw`md:mt-auto`}
  ${tw`text-left`}
`;

export const StyledImage = styled(KontentImage, {
  shouldForwardProp: (prop) => prop !== 'imageSize',
})<{ imageSize?: string | null }>`
  ${tw`shrink-0`}
  ${({ imageSize }) => (imageSize === 'size_128x128' ? tw`h-[128px] w-[128px]` : tw`h-8 w-64`)};
`;

export const StyledContent = styled.div`
  ${tw`flex flex-col gap-2`}
  ${tw`h-full w-full`}
`;

const columnLayouts: Record<
  Queries.kontent_item_column_block_elements_content_layout_enum,
  SerializedStyles
> = {
  vertical: css`
    ${tw`md:col-span-8 lg:col-span-12`}
    ${tw`md:flex-row md:gap-4`}

  ${StyledContent} {
      ${tw`md:gap-1`}
    }
  `,
  horizontal: css`
    ${tw`md:col-span-4 lg:col-span-3`}
    ${tw`md:gap-3`}

  /* Layout for 2 cols */
  &:first-of-type:nth-last-of-type(2),
  &:first-of-type:nth-last-of-type(2) ~ * {
      ${tw`lg:col-span-6`}

      ${StyledContent} {
        ${tw`md:gap-3`}
      }
    }

    /* Layout for 3 cols */
    &:first-of-type:nth-last-of-type(3),
    &:first-of-type:nth-last-of-type(3) ~ * {
      ${tw`lg:col-span-4`}

      ${StyledBody} {
        ${tw`md:mb-1`}
      }
    }

    /* Layout for 6 cols - 3 per row */
    &:first-of-type:nth-last-of-type(6),
    &:first-of-type:nth-last-of-type(6) ~ * {
      ${tw`lg:col-span-4`}

      ${StyledBody} {
        ${tw`md:mb-1`}
      }
    }
  `,
};

export const StyledColumnItem = styled.div<{
  layout: Queries.kontent_item_column_block_elements_content_layout_enum;
}>`
  ${tw`col-span-4`}
  ${tw`flex flex-col gap-3`}

  ${({ layout }) => columnLayouts[layout]}

  picture {
    // Without display:flex, the flex-shrink property will not be applied to the KontentImage. This is only required when the layout is 'vertical'.
    ${({ layout }) =>
      layout === 'vertical' &&
      css`
        ${tw`md:flex`}
      `}
  }
`;
